import React, { useEffect, useState } from 'react';
import api from '../api';

const empty = {
  part_number: '', customer_id: '', length: '', width: '', depth: '',
  flute: '', grade: '', ect: '', mullen: '', white_liner: false, wax: false,
  unit_price: '',
  product_style: '', print_required: '', joint_type: '',
};

const PRODUCT_STYLES = ['RSC', 'HSC', '2PC RSC', '2PC HSC', '5PF', 'Pad', 'Slotted Pad', 'Scored Pad', 'Diecut', 'Teletwin', 'BUB', 'Kit', 'Partition'];
const PRINT_OPTIONS = ['Yes', 'No'];
const JOINT_TYPES = ['G/I', 'G/O', 'Center Seam G/I', 'Taped', 'Stitched'];

export default function PartsMaster({ role }) {
  const [parts, setParts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sel, setSel] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.get('/api/parts').then(r => setParts(r.data || []));
  useEffect(() => { load(); api.get('/api/customers').then(r => setCustomers(r.data || [])); }, []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ flex: 1 }}>Parts Master</h2>
        <button className="btn-success" onClick={() => setAdding(true)}>+ Add Part</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead><tr>
            <th>Part Number</th><th>Customer</th><th>L × W × D</th><th>Flute</th><th>Grade</th><th>ECT</th><th>Mullen</th><th>Product Style</th><th>Print</th><th>Joint</th><th>White Liner</th><th>Wax</th>
          </tr></thead>
          <tbody>
            {parts.map(p => (
              <tr key={p.id} className="clickable" onClick={() => setSel(p)}>
                <td>{p.part_number}</td><td>{p.customer_name}</td>
                <td>{p.length} × {p.width} × {p.depth}</td>
                <td>{p.flute}</td><td>{p.grade}</td><td>{p.ect}</td><td>{p.mullen}</td>
                <td>{p.product_style}</td><td>{p.print_required}</td><td>{p.joint_type}</td>
                <td>{p.white_liner ? '✓' : ''}</td><td>{p.wax ? '✓' : ''}</td>
              </tr>
            ))}
            {parts.length === 0 && <tr><td colSpan={12} style={{ textAlign: 'center', padding: 24, color: '#888' }}>No parts.</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && <PartModal part={sel} customers={customers} onClose={() => setSel(null)} onSaved={() => { load(); setSel(null); }} />}
      {adding && <PartModal part={empty} isNew customers={customers} onClose={() => setAdding(false)} onSaved={() => { load(); setAdding(false); }} />}
    </div>
  );
}

function PartModal({ part, isNew, customers, onClose, onSaved }) {
  const [form, setForm] = useState(part);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tierQty, setTierQty] = useState('');
  const [tierPrice, setTierPrice] = useState('');
  const [tierErr, setTierErr] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [saving, setSaving] = useState(false);

  const loadTiers = React.useCallback(() => {
    if (isNew || !part.id) return;
    api.get(`/api/parts/${part.id}/pricing`).then(r => setTiers(r.data || [])).catch(() => setTiers([]));
  }, [isNew, part.id]);
  useEffect(() => { loadTiers(); }, [loadTiers]);

  const addTier = async () => {
    setTierErr('');
    if (!tierQty || !tierPrice) { setTierErr('Both fields required.'); return; }
    try {
      await api.post(`/api/parts/${part.id}/pricing`, { min_qty: parseInt(tierQty, 10), price: parseFloat(tierPrice) });
      setTierQty(''); setTierPrice('');
      loadTiers();
    } catch (err) {
      setTierErr(err?.response?.data?.error || 'Failed to add tier.');
    }
  };

  const deleteTier = async (tierId) => {
    try {
      await api.delete(`/api/parts/pricing/${tierId}`);
      loadTiers();
    } catch {}
  };

  const loadFiles = React.useCallback(() => {
    if (isNew || !part.id) return;
    api.get(`/api/uploads/parts/${part.id}`).then(r => setFiles(r.data || [])).catch(() => setFiles([]));
  }, [isNew, part.id]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadMsg('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/api/uploads/parts/${part.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadFiles();
    } catch (err) {
      setUploadMsg(err?.response?.data?.error || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaveErr('');
    setSaving(true);
    try {
      const payload = { ...form };
      if (isNew) await api.post('/api/parts', payload);
      else await api.put(`/api/parts/${part.id}`, payload);
      onSaved();
    } catch (err) {
      setSaveErr(err?.response?.data?.error || err?.message || 'Failed to save part.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'Add Part' : form.part_number}</h3>
        <div className="grid2">
          <div className="field"><label>Part Number</label><input value={form.part_number || ''} onChange={e => upd('part_number', e.target.value)} /></div>
          <div className="field"><label>Customer</label>
            <select value={form.customer_id || ''} onChange={e => upd('customer_id', e.target.value)}>
              <option value="">Select…</option>{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Length</label><input type="number" step="0.01" value={form.length || ''} onChange={e => upd('length', e.target.value)} /></div>
          <div className="field"><label>Width</label><input type="number" step="0.01" value={form.width || ''} onChange={e => upd('width', e.target.value)} /></div>
          <div className="field"><label>Depth</label><input type="number" step="0.01" value={form.depth || ''} onChange={e => upd('depth', e.target.value)} /></div>
          <div className="field"><label>Flute</label><input value={form.flute || ''} onChange={e => upd('flute', e.target.value)} /></div>
          <div className="field"><label>Grade</label><input value={form.grade || ''} onChange={e => upd('grade', e.target.value)} /></div>
          <div className="field"><label>ECT</label><input value={form.ect || ''} onChange={e => upd('ect', e.target.value)} /></div>
          <div className="field"><label>Mullen</label><input value={form.mullen || ''} onChange={e => upd('mullen', e.target.value)} /></div>
          <div className="field"><label>Product Style</label>
            <select value={form.product_style || ''} onChange={e => upd('product_style', e.target.value)}>
              <option value="">Select…</option>{PRODUCT_STYLES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label>Print Required</label>
            <select value={form.print_required || ''} onChange={e => upd('print_required', e.target.value)}>
              <option value="">Select…</option>{PRINT_OPTIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label>Joint Type</label>
            <select value={form.joint_type || ''} onChange={e => upd('joint_type', e.target.value)}>
              <option value="">Select…</option>{JOINT_TYPES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="field"><label><input type="checkbox" checked={!!form.white_liner} onChange={e => upd('white_liner', e.target.checked)} /> White Liner</label></div>
          <div className="field"><label><input type="checkbox" checked={!!form.wax} onChange={e => upd('wax', e.target.checked)} /> Wax</label></div>
        </div>
        {!isNew && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '8px 0' }}>Tiered Pricing</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>Min Qty</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #ddd' }}>Price</th>
                  <th style={{ width: 40, borderBottom: '1px solid #ddd' }}></th>
                </tr>
              </thead>
              <tbody>
                {tiers.length === 0 && (
                  <tr><td colSpan={3} style={{ padding: '6px 8px', color: '#888', fontSize: 13 }}>No tiers yet.</td></tr>
                )}
                {tiers.map(t => (
                  <tr key={t.id}>
                    <td style={{ padding: '4px 8px' }}>{t.min_qty}</td>
                    <td style={{ padding: '4px 8px' }}>${Number(t.price).toFixed(4)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                      <button
                        onClick={() => deleteTier(t.id)}
                        style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}
                        title="Remove tier"
                      >✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div className="field" style={{ margin: 0 }}>
                <label>Min Qty</label>
                <input type="number" min="1" step="1" value={tierQty} onChange={e => setTierQty(e.target.value)} style={{ width: 90 }} />
              </div>
              <div className="field" style={{ margin: 0 }}>
                <label>Price</label>
                <input type="number" min="0" step="0.0001" value={tierPrice} onChange={e => setTierPrice(e.target.value)} style={{ width: 100 }} />
              </div>
              <button className="btn-success" onClick={addTier}>Add Tier</button>
            </div>
            {tierErr && <div style={{ color: '#c0392b', marginTop: 6, fontSize: 13 }}>{tierErr}</div>}
          </div>
        )}
        {!isNew && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ margin: '8px 0' }}>Files</h4>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer.files && e.dataTransfer.files[0]) uploadFile(e.dataTransfer.files[0]);
              }}
              onClick={() => document.getElementById(`file-input-${part.id}`).click()}
              style={{
                border: `2px dashed ${dragOver ? '#3498db' : '#bbb'}`,
                background: dragOver ? '#eaf4fb' : '#fafbfc',
                padding: 20, textAlign: 'center', borderRadius: 6, cursor: 'pointer',
                color: '#555',
              }}
            >
              {uploading ? 'Uploading…' : 'Drag & drop a file here, or click to select (PDF, JPEG, JPG, SVG — max 10 MB)'}
              <input
                id={`file-input-${part.id}`}
                type="file"
                accept=".pdf,.jpg,.jpeg,.svg,application/pdf,image/jpeg,image/svg+xml"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) uploadFile(e.target.files[0]); e.target.value = ''; }}
              />
            </div>
            {uploadMsg && <div style={{ color: '#c0392b', marginTop: 6, fontSize: 13 }}>{uploadMsg}</div>}
            <ul style={{ marginTop: 10, paddingLeft: 18 }}>
              {files.length === 0 && <li style={{ color: '#888', listStyle: 'none', marginLeft: -18 }}>No files uploaded yet.</li>}
              {files.map(f => (
                <li key={f.filename}>
                  <a href={f.url} target="_blank" rel="noreferrer">{f.filename}</a>
                  <span style={{ color: '#888', fontSize: 12, marginLeft: 8 }}>({Math.round((f.size || 0) / 1024)} KB)</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {saveErr && <div style={{ color: '#c0392b', marginTop: 12, fontSize: 13 }}>{saveErr}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn-success" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
